import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { IftaLabel } from 'primeng/iftalabel';
import { TableModule } from 'primeng/table';
import { TreeSelect } from 'primeng/treeselect';
import { TreeNode } from 'primeng/api';
import { TreeNodeSelectEvent } from 'primeng/tree';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { ButtonGroup } from 'primeng/buttongroup';
import { Tooltip } from 'primeng/tooltip';
import {
  BdExportColumnOption,
  BdExportColumnOptions,
} from './export-column-option';

interface ColumnRow {
  node: TreeNode;
  columnId: string;
}

/**
 * Presentational, reorderable column picker for an export template. The catalog
 * is supplied via {@link columns} (already adapted to {@link BdExportColumnOption}),
 * so this component is free of any API/service/domain coupling and is shared by
 * the virtual-clinic and clinics-filter export-template forms.
 */
@Component({
  selector: 'bd-export-template-columns-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-table [value]="rows()" (onRowReorder)="onRowReorderChange()">
      <ng-template #header>
        <tr>
          <th style="width:3rem"></th>
          <th>Spalte</th>
          <th>Inhalt</th>
          <th style="width: 5rem"></th>
        </tr>
      </ng-template>
      <ng-template #body let-rowData let-index="rowIndex">
        <tr [pReorderableRow]="index">
          <td>
            <span class="pi pi-bars" pReorderableRowHandle></span>
          </td>
          <td>{{ index + 1 }}. Spalte</td>
          <td>
            <p-tree-select
              inputId="{{ idPrefix() }}-column-{{ index }}"
              variant="filled"
              fluid
              filter
              [ngModel]="rowData.node"
              [options]="availableOptionsForInlineChange()"
              (ngModelChange)="onRowValueChange(index, $event)"
            />
          </td>
          <td style="text-align: end">
            <p-buttongroup>
              <p-button
                severity="secondary"
                icon="pi pi-chevron-up"
                pTooltip="Spalte nach oben verschieben"
                tooltipPosition="left"
                [disabled]="!(index > 0)"
                [text]="true"
                (onClick)="onRowMoveUp(index)"
              ></p-button>
              <p-button
                severity="secondary"
                icon="pi pi-chevron-down"
                pTooltip="Spalte nach unten verschieben"
                tooltipPosition="left"
                [disabled]="!(rows().length > index + 1)"
                [text]="true"
                (onClick)="onRowMoveDown(index)"
              ></p-button>
              <p-button
                severity="danger"
                icon="pi pi-trash"
                pTooltip="Spalte entfernen"
                tooltipPosition="left"
                [text]="true"
                (onClick)="onRowDelete(index)"
              ></p-button>
            </p-buttongroup>
          </td>
        </tr>
      </ng-template>
      <ng-template #footer>
        <tr>
          <td></td>
          <td colspan="2">
            <p-iftalabel class="basis-128">
              <p-tree-select
                fluid
                inputId="{{ idPrefix() }}-new-columns"
                variant="filled"
                filter
                [ngModel]="currentNewNodes()"
                (ngModelChange)="currentNewNodes.set($event)"
                [options]="availableOptionsForNewWizard()"
                (onNodeSelect)="onNewColumnsChange($event)"
              />
              <label for="{{ idPrefix() }}-new-columns"
                >Neue Spalte hinzufügen</label
              >
            </p-iftalabel>
          </td>
          <td></td>
        </tr>
      </ng-template>
    </p-table>
  `,
  standalone: true,
  imports: [
    IftaLabel,
    TableModule,
    TreeSelect,
    FormsModule,
    Button,
    ButtonGroup,
    Tooltip,
  ],
})
export class BdExportTemplateColumnsFormComponent {
  public readonly columns = input<BdExportColumnOption[]>([]);
  public readonly propertyIds = input<string[]>([]);
  public readonly idPrefix = input<string>('export-template-form');
  public readonly propertyIdsChange = output<string[]>();

  public readonly currentNewNodes = signal<TreeNode | undefined>(undefined);
  public readonly rows = signal<ColumnRow[]>([]);

  public readonly availableOptionsForNewWizard = computed(() =>
    this.columns().map((option) => this._toTreeNode(option, () => true)),
  );

  public readonly availableOptionsForInlineChange = computed(() =>
    this.columns().map((option) =>
      this._toTreeNode(option, (candidate) => candidate.selectable),
    ),
  );

  constructor() {
    effect(() => {
      const columns = this.columns();
      if (columns.length === 0) return;
      this.rows.set(
        this.propertyIds()
          .map((columnId) => this._createRow(columnId, columns))
          .filter((row): row is ColumnRow => row !== null),
      );
    });
  }

  public onRowReorderChange(): void {
    this._sendToOutput();
  }

  public onRowValueChange(rowIndex: number, newValue: TreeNode): void {
    this.rows.update((current) => {
      const updated = [...current];
      updated[rowIndex] = {
        ...updated[rowIndex],
        node: newValue,
        columnId: newValue.key ?? '',
      };
      return updated;
    });
    this._sendToOutput();
  }

  public onRowDelete(rowIndex: number): void {
    this.rows.update((current) => current.filter((_, i) => i !== rowIndex));
    this._sendToOutput();
  }

  public onRowMoveUp(rowIndex: number): void {
    this.rows.update((current) => this._move(current, rowIndex, rowIndex - 1));
    this._sendToOutput();
  }

  public onRowMoveDown(rowIndex: number): void {
    this.rows.update((current) => this._move(current, rowIndex, rowIndex + 1));
    this._sendToOutput();
  }

  public onNewColumnsChange(event: TreeNodeSelectEvent): void {
    const columns = this.columns();
    const newRows = this._flatAllLeafNodes(event.node)
      .map((node: TreeNode) => this._createRow(node.key ?? '', columns))
      .filter((row): row is ColumnRow => row !== null);
    this.rows.update((current) => [...current, ...newRows]);
    this.currentNewNodes.set(undefined);
    this._sendToOutput();
  }

  private _move(rows: ColumnRow[], from: number, to: number): ColumnRow[] {
    const updated = [...rows];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    return updated;
  }

  private _sendToOutput(): void {
    this.propertyIdsChange.emit(this.rows().map((row) => row.columnId));
  }

  private _createRow(
    columnId: string,
    columns: BdExportColumnOption[],
  ): ColumnRow | null {
    const option = BdExportColumnOptions.findById(columns, columnId);
    if (!option) {
      return null;
    }
    const parent = BdExportColumnOptions.findParentById(columns, columnId);
    const label = parent
      ? `${parent.displayName} / ${option.displayName}`
      : option.displayName;
    return {
      node: {
        key: option.id,
        label,
        selectable: option.selectable,
      } as TreeNode,
      columnId: option.id,
    };
  }

  private _toTreeNode(
    option: BdExportColumnOption,
    selectableFn: (option: BdExportColumnOption) => boolean,
  ): TreeNode {
    const children = option.children ?? [];
    return {
      key: option.id,
      label: option.displayName,
      leaf: children.length === 0,
      selectable: selectableFn(option),
      children: children.map((child) => this._toTreeNode(child, selectableFn)),
    };
  }

  private _flatAllLeafNodes(node: TreeNode): TreeNode[] {
    if (node.leaf) {
      return [node];
    }
    return (node.children ?? [])
      .map((childNode) => this._flatAllLeafNodes(childNode))
      .flat();
  }
}
